import { DatePicker, Popover, Select } from 'antd';
// import moment, { Moment, unitOfTime } from 'moment';
import { LeftOutlined, RightOutlined, SwapRightOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
// import 'moment-timezone';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { EReportDataInterval } from '../screens/report/types/report-log.type';

type ValuePiece = Date | null | Dayjs;

export type Value = ValuePiece | [ValuePiece, ValuePiece];

interface IProps {
  // timezone(timezone: any): unknown;
  custom: boolean;
  startAt: number;
  endAt: number;
  timezone: number;
  disabled?: boolean;
  onChange: (e: any) => void;
  onChangeCustomTime: (e: Dayjs, type: 'start' | 'end') => void;
  onChangeCustomDateRange: (date: Value) => void;
  // constanta: number;
  // timezone: string;
  interval: EReportDataInterval;
  // longest range (days, inclusive) the calendar lets the user pick
  maxRangeDays?: number;
}

const { RangePicker } = DatePicker;
const dateTimeFormat = 'DD/MM/YYYY HH:mm';
const dateFormat = 'YYYY-MM-DD';
const monthFormat = 'MMM YYYY';
const monthTileFormat = 'MMM';
const miliseconds = 1000;
const monthHistoryWindowInYears = 1;
const calendarRangeBlue = '#264284';
const calendarActiveBlue = '#7996da';
const intervalWithoutTime = [
  EReportDataInterval.day,
  EReportDataInterval.week,
  EReportDataInterval.month,
];

dayjs.extend(utc);

const DateRangePicker: React.FC<IProps> = (props) => {
  const selectedStartMonth = useMemo(
    () =>
      dayjs(props.startAt * miliseconds)
        .utcOffset(props.timezone * 60)
        .startOf('month'),
    [props.startAt, props.timezone]
  );
  const selectedEndMonth = useMemo(
    () =>
      dayjs(props.endAt * miliseconds)
        .utcOffset(props.timezone * 60)
        .startOf('month'),
    [props.endAt, props.timezone]
  );
  const maxSelectableMonth = useMemo(
    () =>
      dayjs()
        .utcOffset(props.timezone * 60)
        .startOf('month'),
    [props.timezone]
  );
  const minSelectableMonth = useMemo(
    () =>
      maxSelectableMonth
        .subtract(monthHistoryWindowInYears, 'year')
        .startOf('year'),
    [maxSelectableMonth]
  );
  const [activeMonthYear, setActiveMonthYear] = useState(selectedStartMonth.year());
  const [monthRangeAnchor, setMonthRangeAnchor] = useState<Dayjs | null>(null);
  const [dayRangeAnchor, setDayRangeAnchor] = useState<Dayjs | null>(null);
  const minSelectableYear = minSelectableMonth.year();
  const maxSelectableYear = maxSelectableMonth.year();

  useEffect(() => {
    const nextYear = Math.min(
      maxSelectableYear,
      Math.max(minSelectableYear, selectedStartMonth.year())
    );

    setActiveMonthYear(nextYear);
    setMonthRangeAnchor(null);
  }, [
    maxSelectableYear,
    minSelectableYear,
    selectedStartMonth,
  ]);

  const visibleMonths = useMemo(() => {
    return [...Array(12).keys()]
      .map((monthIndex) => dayjs(new Date(activeMonthYear, monthIndex, 1)))
      .filter(
        (month) =>
          !month.isBefore(minSelectableMonth, 'month') &&
          !month.isAfter(maxSelectableMonth, 'month')
      );
  }, [activeMonthYear, maxSelectableMonth, minSelectableMonth]);

  const hour = useMemo(() => {
    return [...Array(24).keys()].map((item) => ({
      label: item,
      value: item,
    }));
  }, []);

  const minute = useMemo(() => {
    return [...Array(60).keys()].map((item) => ({
      label: item,
      value: item,
    }));
  }, []);

  function onChangeTime(
    value: number,
    type: 'start' | 'end',
    timeType: 'hour' | 'minute'
  ) {
    const current = dayjs(
      type === 'start' ? props.startAt * 1000 : props.endAt * 1000
    )
      .utcOffset(props.timezone * 60)
      .set(timeType, value);
    props.onChangeCustomTime(current, type);
  }

  const range = useMemo(() => {
    const startDate = dayjs(props.startAt * miliseconds).utcOffset(
      props.timezone * 60
    );
    const endDate = dayjs(props.endAt * miliseconds).utcOffset(
      props.timezone * 60
    );
    const start = {
      dateTimeFormat: startDate.format(dateTimeFormat),
      dateFormat: startDate.format(dateFormat),
      monthFormat: startDate.format(monthFormat),
      hour: startDate.hour(),
      minute: startDate.minute(),
      unix: props.startAt,
    };
    const end = {
      dateTimeFormat: endDate.format(dateTimeFormat),
      dateFormat: endDate.format(dateFormat),
      monthFormat: endDate.format(monthFormat),
      hour: endDate.hour(),
      minute: endDate.minute(),
      unix: props.endAt,
    };
    return {
      start,
      end,
    };
  }, [props.startAt, props.endAt, props.timezone]);

  const isMonthInSelectedRange = (month: Dayjs) => {
    return (
      (month.isAfter(selectedStartMonth, 'month') ||
        month.isSame(selectedStartMonth, 'month')) &&
      (month.isBefore(selectedEndMonth, 'month') ||
        month.isSame(selectedEndMonth, 'month'))
    );
  };

  const handleChangeMonthRange = (month: Dayjs) => {
    if (!monthRangeAnchor) {
      setMonthRangeAnchor(month);
      return;
    }

    const normalizedStart = month.isBefore(monthRangeAnchor, 'month')
      ? month
      : monthRangeAnchor;
    const normalizedEnd = month.isBefore(monthRangeAnchor, 'month')
      ? monthRangeAnchor
      : month;

    setMonthRangeAnchor(null);
    props.onChangeCustomDateRange([
      normalizedStart.toDate(),
      normalizedEnd.toDate(),
    ]);
  };

  const renderMonthRangePicker = () => {
    return (
      <MonthPickerPanel>
        <MonthPickerHeader>
          <MonthNavigationButton
            type='button'
            disabled={activeMonthYear <= minSelectableYear}
            onClick={() =>
              setActiveMonthYear((currentYear) =>
                Math.max(minSelectableYear, currentYear - 1)
              )
            }
          >
            <LeftOutlined />
          </MonthNavigationButton>
          <MonthPickerTitle>{activeMonthYear}</MonthPickerTitle>
          <MonthNavigationButton
            type='button'
            disabled={activeMonthYear >= maxSelectableYear}
            onClick={() =>
              setActiveMonthYear((currentYear) =>
                Math.min(maxSelectableYear, currentYear + 1)
              )
            }
          >
            <RightOutlined />
          </MonthNavigationButton>
        </MonthPickerHeader>
        <MonthGrid>
          {visibleMonths.map((month) => {
            const isRangeStart = month.isSame(selectedStartMonth, 'month');
            const isRangeEnd = month.isSame(selectedEndMonth, 'month');
            const isPending = monthRangeAnchor?.isSame(month, 'month');

            return (
              <MonthGridButton
                key={month.format('YYYY-MM')}
                type='button'
                $isRangeEdge={isRangeStart || isRangeEnd}
                $isSelected={isMonthInSelectedRange(month)}
                $isPending={Boolean(isPending)}
                onClick={() => handleChangeMonthRange(month)}
              >
                {month.format(monthTileFormat)}
              </MonthGridButton>
            );
          })}
        </MonthGrid>
      </MonthPickerPanel>
    );
  };

  if (props.interval === EReportDataInterval.month) {
    return (
      <ContainerRangePicker>
        <Popover
          open={props.disabled ? false : undefined}
          placement='bottomLeft'
          content={renderMonthRangePicker()}
          trigger='click'
        >
          <CalendarInput disabled={props.disabled}>
            {range.start.monthFormat}
            <SwapRightOutlined />
            {range.end.monthFormat}
          </CalendarInput>
        </Popover>
      </ContainerRangePicker>
    );
  }

  if (!props.custom) {
    return (
      <RangePicker
        style={{ width: '100%' }}
        disabled={props.disabled}
        showTime
        value={[
          dayjs(props.startAt * miliseconds).utcOffset(props.timezone * 60),
          dayjs(props.endAt * miliseconds).utcOffset(props.timezone * 60),
        ]}
        format={dateTimeFormat}
        onChange={props.onChange}
      />
    );
  }

  function onChangeCalendar(value: Value) {
    setDayRangeAnchor(null);
    if (value) {
      props.onChangeCustomDateRange(value);
    }
  }

  // the second click of a range ends it, so only the first one is the anchor
  function onClickCalendarDay(date: Date) {
    setDayRangeAnchor((anchor) => (anchor ? null : dayjs(date)));
  }

  function isCalendarDayDisabled({ date }: { date: Date }) {
    if (!props.maxRangeDays || !dayRangeAnchor) {
      return false;
    }

    return (
      Math.abs(dayjs(date).startOf('day').diff(dayRangeAnchor.startOf('day'), 'day')) >=
      props.maxRangeDays
    );
  }

  const content = () => {
    return (
      <>
        <div style={{ width: 250 }}>
          <Calendar
            className='custom-calendar'
            selectRange
            returnValue='range'
            onChange={onChangeCalendar}
            onClickDay={onClickCalendarDay}
            tileDisabled={isCalendarDayDisabled}
            value={[range.start.dateFormat, range.end.dateFormat]}
          />
        </div>

        {!intervalWithoutTime.includes(props.interval) && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <div style={{ flex: 1 }}>
              <div>Start time</div>
              <WrapperTime>
                <div>
                  <Select
                    size='small'
                    options={hour}
                    value={range.start.hour}
                    onSelect={(e) => onChangeTime(e, 'start', 'hour')}
                  />
                </div>
                <span>:</span>
                <div>
                  <Select
                    size='small'
                    options={minute}
                    value={range.start.minute}
                    onSelect={(e) => onChangeTime(e, 'start', 'minute')}
                  />
                </div>
              </WrapperTime>
            </div>
            <div style={{ flex: 1 }}>
              <div>End time</div>
              <WrapperTime>
                <div>
                  <Select
                    size='small'
                    options={hour}
                    value={range.end.hour}
                    onSelect={(e) => onChangeTime(e, 'end', 'hour')}
                  />
                </div>
                <span>:</span>
                <div>
                  <Select
                    size='small'
                    options={minute}
                    value={range.end.minute}
                    onSelect={(e) => onChangeTime(e, 'end', 'minute')}
                  />
                </div>
              </WrapperTime>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <ContainerRangePicker>
      <Popover
        open={props.disabled ? false : undefined}
        placement='bottom'
        content={() => content()}
        trigger='click'
      >
        <CalendarInput disabled={props.disabled}>
          {/* {moment(range.start.raw).tz(props.timezone).format(dateTimeFormat)} */}
          {range.start.dateTimeFormat}
          <SwapRightOutlined />
          {/* {moment(range.end.raw).tz(props.timezone).format(dateTimeFormat)} */}
          {range.end.dateTimeFormat}
        </CalendarInput>
      </Popover>
    </ContainerRangePicker>
  );
};

const WrapperTime = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: 1fr auto 1fr;
  gap: 5px;
`;

const MonthPickerPanel = styled.div`
  width: 280px;
`;

const MonthPickerHeader = styled.div`
  display: grid;
  grid-template-columns: 32px 1fr 32px;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const MonthPickerTitle = styled.div`
  text-align: center;
  font-size: 14px;
  font-weight: 600;
`;

const MonthNavigationButton = styled.button`
  width: 32px;
  height: 32px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: white;
  color: #262626;
  cursor: pointer;

  &:disabled {
    color: #bfbfbf;
    cursor: not-allowed;
  }
`;

const MonthGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
`;

interface IMonthGridButton {
  $isRangeEdge?: boolean;
  $isSelected?: boolean;
  $isPending?: boolean;
}

const MonthGridButton = styled.button<IMonthGridButton>`
  min-height: 40px;
  border: 1px solid
    ${(props) =>
      props.$isRangeEdge || props.$isPending
        ? calendarRangeBlue
        : props.$isSelected
        ? calendarActiveBlue
        : '#d9d9d9'};
  border-radius: 6px;
  background: ${(props) => {
    if (props.$isRangeEdge || props.$isPending) {
      return calendarRangeBlue;
    }

    if (props.$isSelected) {
      return calendarActiveBlue;
    }

    return 'white';
  }};
  color: ${(props) =>
    props.$isRangeEdge || props.$isPending || props.$isSelected
      ? 'white'
      : '#262626'};
  cursor: pointer;
  font-size: 13px;
  font-weight: ${(props) =>
    props.$isRangeEdge || props.$isPending ? 600 : 500};
`;

const ContainerRangePicker = styled.div`
  width: 100%;
`;

interface ICalendarInput {
  disabled?: boolean;
}

const CalendarInput = styled.div<ICalendarInput>`
  border: 1px solid #d9d9d9;
  padding: 0 11px;
  height: 32px;
  border-radius: 5px;
  background-color: ${(props) => (props.disabled ? 'lightgray' : 'white')};
  color: ${(props) => (props.disabled ? 'gray' : 'black')};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 5px;
  align-items: center;
  font-size: 13px;
  justify-content: space-between;
`;

export default DateRangePicker;
